import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { localeFromPublicPathname, SUPPORTED_LOCALES } from '../../i18n/config';
import {
  localeHomePath,
  localePublicPagePath,
  SITE_NAME,
  SITE_URL,
  LLMS_FULL_TXT_URL,
} from '../../constants/siteSeo';

/** hreflang 用（zh-CN → zh-Hans） */
const HREFLANG_MAP: Record<string, string> = {
  'zh-CN': 'zh-Hans',
};

export interface PageMetaProps {
  /** i18n key for document title (e.g. "home.title") */
  titleKey: string;
  /** i18n key for meta description (e.g. "home.meta_description") */
  descriptionKey: string;
  /**
   * Path segment for canonical and og:url (no leading slash).
   * For locale-specific home use current locale (e.g. "en"; ja 正規は空文字で `/`）。
   * For other pages use path (e.g. "login", "register", "help").
   */
  path: string;
  /**
   * When true, canonical and hreflang use locale-aware URLs.
   * ja は無印 `/{path}`、他は `/{locale}/{path}`（Gaisuke / ホーム方針に合わせる）。
   */
  localeAware?: boolean;
  /**
   * Bare public pages (/help, /contact): canonical is /{path}, and emit hreflang
   * (ja → /{path}, others → /{locale}/{path}) plus x-default /{path}.
   */
  localeAlternates?: boolean;
  /** Home (/) and /{locale} tops: emit hreflang alternates for all supported locales. */
  homeAlternates?: boolean;
  /**
   * Locale-less segment (e.g. "arena/econ/vietnam-investment-2026") used only to emit
   * hreflang alternates. `path` still decides canonical, so the URL actually being
   * viewed stays canonical even when the UI language differs from the path.
   */
  alternatesPath?: string;
  /** Optional i18n key for meta keywords (home SEO). */
  keywordsKey?: string;
  /** Public pages: link to /llms-full.txt for AI/search agents. */
  llmsDescribedBy?: boolean;
  /**
   * robots meta. Overrides the default index,follow used for home / llmsDescribedBy pages.
   * Auth pages (login/register/password-reset) should pass "noindex,follow".
   */
  robots?: 'index,follow' | 'noindex,follow' | 'noindex,nofollow';
}

/**
 * Sets document title, meta description, canonical URL, and og:* per page/locale.
 * Use on each public page so search results and shares show the right language.
 */
export default function PageMeta({
  titleKey,
  descriptionKey,
  path,
  localeAware,
  localeAlternates,
  homeAlternates,
  alternatesPath,
  keywordsKey,
  llmsDescribedBy,
  robots,
}: PageMetaProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const title = t(titleKey, { defaultValue: SITE_NAME });
  const description = t(descriptionKey, { defaultValue: '' });
  const keywords = keywordsKey ? t(keywordsKey, { defaultValue: '' }) : '';
  const locale = localeFromPublicPathname(location.pathname) || i18n.language;
  const isHomePath =
    homeAlternates &&
    (path === '' || SUPPORTED_LOCALES.includes(path as (typeof SUPPORTED_LOCALES)[number]));
  const documentLang =
    isHomePath && path && SUPPORTED_LOCALES.includes(path as (typeof SUPPORTED_LOCALES)[number])
      ? path
      : isHomePath && !path
        ? 'ja'
        : locale;

  const canonicalUrl = isHomePath
    ? path
      ? `${SITE_URL}/${path}`
      : `${SITE_URL}/`
    : localeAware
      ? `${SITE_URL}${localePublicPagePath(locale, path)}`
      : path
        ? `${SITE_URL}/${path}`
        : `${SITE_URL}/`;

  const displayTitle =
    title && (title === SITE_NAME || title.includes(SITE_NAME))
      ? title
      : title
        ? `${title} | ${SITE_NAME}`
        : SITE_NAME;
  const ogLocale = documentLang === 'zh-CN' ? 'zh_CN' : documentLang.replace('-', '_');

  const homeHref = (loc: string) => {
    const p = localeHomePath(loc);
    return p === '/' ? `${SITE_URL}/` : `${SITE_URL}${p}`;
  };

  const pageHref = (loc: string) => `${SITE_URL}${localePublicPagePath(loc, path)}`;
  const alternateHref = (loc: string) =>
    `${SITE_URL}${localePublicPagePath(loc, alternatesPath ?? '')}`;

  const robotsContent =
    robots ?? (isHomePath || llmsDescribedBy ? 'index,follow' : undefined);

  return (
    <Helmet>
      <html lang={documentLang} />
      <title>{displayTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="new_kuu21" />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={displayTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={displayTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
      {robotsContent && <meta name="robots" content={robotsContent} />}
      {(llmsDescribedBy || isHomePath) && (
        <link rel="describedby" type="text/plain" href={LLMS_FULL_TXT_URL} />
      )}
      {isHomePath &&
        SUPPORTED_LOCALES.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={HREFLANG_MAP[loc] ?? loc}
            href={homeHref(loc)}
          />
        ))}
      {isHomePath && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />}
      {localeAware &&
        path &&
        SUPPORTED_LOCALES.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={HREFLANG_MAP[loc] ?? loc}
            href={pageHref(loc)}
          />
        ))}
      {localeAware && path && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/${path}`} />
      )}
      {!localeAware &&
        localeAlternates &&
        path &&
        SUPPORTED_LOCALES.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={HREFLANG_MAP[loc] ?? loc}
            href={pageHref(loc)}
          />
        ))}
      {!localeAware && localeAlternates && path && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/${path}`} />
      )}
      {alternatesPath &&
        SUPPORTED_LOCALES.map((loc) => (
          <link
            key={`alt-${loc}`}
            rel="alternate"
            hrefLang={HREFLANG_MAP[loc] ?? loc}
            href={alternateHref(loc)}
          />
        ))}
      {alternatesPath && (
        <link rel="alternate" hrefLang="x-default" href={alternateHref('ja')} />
      )}
    </Helmet>
  );
}
